import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Eye, EyeOff, Copy, Check, AlertCircle } from 'lucide-react';

const TestVariablesManager = ({
  testVariables,
  sensitiveVariables,
  onUpdate,
  testSteps = []
}) => {
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  const [newVariableIsSensitive, setNewVariableIsSensitive] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [showValues, setShowValues] = useState({});
  const [copiedVar, setCopiedVar] = useState(null);
  const variableUsage = React.useMemo(() => {
    const usage = {};
    Object.keys(testVariables || {}).forEach(varName => {
      usage[varName] = {
        count: 0,
        steps: []
      };
    });

    (testSteps || []).forEach((step, index) => {
      const stepValue = step.value || '';
      Object.keys(testVariables || {}).forEach(varName => {
        if (stepValue.includes(`{{${varName}}}`)) {
          usage[varName].count++;
          usage[varName].steps.push(index + 1);
        }
      });
    });

    return usage;
  }, [testVariables, testSteps]);

  const maskSensitiveValue = (value) => {
    if (!value) return value;
    const len = value.length;
    if (len <= 2) return '•'.repeat(len);
    if (len <= 8) return value[0] + '•'.repeat(len - 2) + value[len - 1];
    return value.substring(0, 2) + '•'.repeat(len - 4) + value.substring(len - 2);
  };

  const handleAddOrUpdate = () => {
    if (!newVariableName.trim()) {
      alert('Variable name is required');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(newVariableName)) {
      alert('Variable name can only contain letters, numbers, and underscores');
      return;
    }

    const updatedVariables = { ...testVariables };
    updatedVariables[newVariableName] = newVariableValue;

    const updatedSensitiveVars = [...sensitiveVariables];
    if (newVariableIsSensitive && !updatedSensitiveVars.includes(newVariableName)) {
      updatedSensitiveVars.push(newVariableName);
    } else if (!newVariableIsSensitive && updatedSensitiveVars.includes(newVariableName)) {
      const index = updatedSensitiveVars.indexOf(newVariableName);
      updatedSensitiveVars.splice(index, 1);
    }

    onUpdate(updatedVariables, updatedSensitiveVars);

    // Reset form
    setNewVariableName('');
    setNewVariableValue('');
    setNewVariableIsSensitive(false);
    setEditingVariable(null);
  };

  const handleEdit = (varName) => {
    setEditingVariable(varName);
    setNewVariableName(varName);
    setNewVariableValue(testVariables[varName]);
    setNewVariableIsSensitive(sensitiveVariables.includes(varName));
  };

  const handleDelete = (varName) => {
    if (!window.confirm(`Delete variable "${varName}"?`)) return;

    const updatedVariables = { ...testVariables };
    delete updatedVariables[varName];

    const updatedSensitiveVars = sensitiveVariables.filter(v => v !== varName);

    onUpdate(updatedVariables, updatedSensitiveVars);
  };

  const handleCopy = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const toggleShowValue = (varName) => {
    setShowValues(prev => ({
      ...prev,
      [varName]: !prev[varName]
    }));
  };

  const variableEntries = Object.entries(testVariables || {});

  return (
    <div className="space-y-6">
      {/* Add/Edit Variable Form */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          {editingVariable ? `Edit Variable: ${editingVariable}` : 'Add New Variable'}
        </h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Variable Name *
            </label>
            <input
              type="text"
              value={newVariableName}
              onChange={(e) => setNewVariableName(e.target.value)}
              placeholder="e.g., userEmail, password, apiKey"
              disabled={!!editingVariable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use only letters, numbers, and underscores. No spaces.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Value *
            </label>
            <input
              type="text"
              value={newVariableValue}
              onChange={(e) => setNewVariableValue(e.target.value)}
              placeholder="Enter the variable value"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="sensitive-checkbox"
              checked={newVariableIsSensitive}
              onChange={(e) => setNewVariableIsSensitive(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="sensitive-checkbox" className="text-sm text-gray-700">
              🔒 Mark as sensitive (value will be masked in logs and screenshots)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleAddOrUpdate}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
            >
              {editingVariable ? 'Update Variable' : 'Add Variable'}
            </button>
            {editingVariable && (
              <button
                onClick={() => {
                  setEditingVariable(null);
                  setNewVariableName('');
                  setNewVariableValue('');
                  setNewVariableIsSensitive(false);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Variables List */}
      {variableEntries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium mb-1">No variables defined yet</p>
          <p className="text-sm text-gray-500">
            Create variables to reuse data across multiple test steps
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700">
            Defined Variables ({variableEntries.length})
          </h4>
          <div className="space-y-2">
            {variableEntries.map(([varName, varValue]) => {
              const isSensitive = sensitiveVariables.includes(varName);
              const displayValue = isSensitive && !showValues[varName] 
                ? maskSensitiveValue(varValue) 
                : varValue;
              const usage = variableUsage[varName] || { count: 0, steps: [] };

              return (
                <div
                  key={varName}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <code className="text-sm font-mono font-semibold text-blue-600">
                          {varName}
                        </code>
                        {isSensitive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            🔒 Sensitive
                          </span>
                        )}
                        {usage.count > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Used in {usage.count} step{usage.count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono break-all">
                          {displayValue}
                        </code>
                        {isSensitive && (
                          <button
                            onClick={() => toggleShowValue(varName)}
                            className="text-gray-500 hover:text-gray-700"
                            title={showValues[varName] ? 'Hide value' : 'Show value'}
                          >
                            {showValues[varName] ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {usage.count > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Steps: {usage.steps.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 ml-4">
                      <button
                        onClick={() => handleCopy(varName)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Copy variable reference"
                      >
                        {copiedVar === varName ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(varName)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit variable"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(varName)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete variable"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Usage Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 How to Use Variables</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Reference variables in test steps using <code className="bg-blue-100 px-1 rounded">&#123;&#123;variableName&#125;&#125;</code></li>
          <li>• Sensitive variables are automatically masked in execution logs and screenshots</li>
          <li>• Update a variable value once, and it updates everywhere it&apos;s used</li>
          <li>• Variables are scoped to this test only</li>
        </ul>
      </div>
    </div>
  );
};

export default TestVariablesManager;
